import chalk from "chalk";
import ora from "ora";
import { runCommand } from "./exec.js";

/**
 * Robust port forwarding configuration wrapper.
 * Clears old mappings, attempts mapping, and restarts ADB daemon on failure.
 */
export async function setupAdbReverse(targetDevice: string, port: number): Promise<boolean> {
  const deviceFlag = targetDevice ? `-s ${targetDevice}` : "";

  // 1. Remove any stale/existing mapping (ignore error if none existed)
  await runCommand(`adb ${deviceFlag} reverse --remove tcp:${port}`);

  // 2. Establish fresh reverse port mapping
  const reverseResult = await runCommand(`adb ${deviceFlag} reverse tcp:${port} tcp:${port}`);
  if (!reverseResult.error) {
    console.log(chalk.green(`✓ Port ${port} reversed successfully.`));
    return true;
  }

  // 3. Auto-recovery: If command fails, attempt daemon restart
  console.log(chalk.yellow(`⚠️  adb reverse failed. Attempting ADB server recovery...`));
  const recoverSpinner = ora("Restarting ADB server daemon...").start();
  
  await runCommand("adb kill-server");
  await runCommand("adb start-server");
  
  // Wait 1.5s for ADB to boot up
  await new Promise(resolve => setTimeout(resolve, 1500));
  recoverSpinner.succeed("ADB server daemon restarted.");

  // Re-attempt stale removal & reverse setup
  await runCommand(`adb ${deviceFlag} reverse --remove tcp:${port}`);
  const retryResult = await runCommand(`adb ${deviceFlag} reverse tcp:${port} tcp:${port}`);
  
  if (!retryResult.error) {
    console.log(chalk.green(`✓ Port ${port} reversed successfully after recovery.`));
    return true;
  }

  console.log(chalk.red(`\n✖ Failed to set up port reverse for port ${port}.`));
  console.log(chalk.yellow("💡 Try checking your USB debugging connection or run 'adb devices' manually to troubleshoot."));
  return false;
}
